import { useEffect, useRef, ReactNode } from 'react';

interface InfiniteScrollObserverProps {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  threshold?: number;
  children?: ReactNode;
}

export default function InfiniteScrollObserver({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 0.1,
  children,
}: InfiniteScrollObserverProps) {
  const observerRef = useRef<HTMLDivElement>(null);
  const lastTriggeredPage = useRef<number>(0);

  useEffect(() => {
    // Capture the current ref value inside the effect
    const currentObserverRef = observerRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          console.log('Observer triggered, calling onLoadMore');
          onLoadMore();
          lastTriggeredPage.current += 1;
        }
      },
      { threshold },
    );

    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }

    // Cleanup using the captured ref value
    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [hasMore, isLoading, onLoadMore, threshold]); // Dependencies remain the same

  return (
    <div ref={observerRef} className="h-10">
      {children}
    </div>
  );
}
